import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { ChevronLeft, ChevronRight, Download } from 'lucide-react';
import { format, startOfWeek, addWeeks, subWeeks } from 'date-fns';

const WeeklyReport = () => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [stats, setStats] = useState(null);
  const [rides, setRides] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchWeeklyData();
  }, [currentDate]);

  const fetchWeeklyData = async () => {
    setLoading(true);
    try {
      const weekStart = startOfWeek(currentDate, { weekStartsOn: 0 });
      const statsResponse = await axios.get(`${API}/stats/weekly?date=${weekStart.toISOString()}`);
      setStats(statsResponse.data);

      const ridesResponse = await axios.get(
        `${API}/rides?start_date=${statsResponse.data.week_start}&end_date=${statsResponse.data.week_end}`
      );
      setRides(ridesResponse.data);
    } catch (error) {
      toast.error('Failed to load weekly data');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      const response = await axios.get(
        `${API}/export/csv?start_date=${stats.week_start}&end_date=${stats.week_end}`,
        { responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `weekly_report_${format(currentDate, 'yyyy-MM-dd')}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Report exported successfully');
    } catch (error) {
      toast.error('Failed to export report');
    }
  };

  const goToPreviousWeek = () => setCurrentDate(subWeeks(currentDate, 1));
  const goToNextWeek = () => setCurrentDate(addWeeks(currentDate, 1));
  const goToCurrentWeek = () => setCurrentDate(new Date());

  if (loading && !stats) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-gray-600">Loading report...</div>
      </div>
    );
  }

  const weekStart = stats ? new Date(stats.week_start) : startOfWeek(currentDate, { weekStartsOn: 0 });
  const weekEnd = stats ? new Date(new Date(stats.week_end).getTime() - 1) : new Date();

  return (
    <div className="space-y-6" data-testid="weekly-report-page">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">Weekly Report</h2>
          <p className="text-gray-600 mt-1">
            {format(weekStart, 'MMM dd')} - {format(weekEnd, 'MMM dd, yyyy')}
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="icon"
            onClick={goToPreviousWeek}
            data-testid="previous-week-button"
          >
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <Button
            variant="outline"
            onClick={goToCurrentWeek}
            data-testid="current-week-button"
          >
            This Week
          </Button>
          <Button
            variant="outline"
            size="icon"
            onClick={goToNextWeek}
            data-testid="next-week-button"
          >
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="text-sm text-gray-600 mb-1">Total Rides</div>
            <div className="text-2xl font-bold">{stats?.total_rides || 0}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-sm text-gray-600 mb-1">Total Miles</div>
            <div className="text-2xl font-bold">{stats?.total_miles.toFixed(1) || 0}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-sm text-gray-600 mb-1">Gross Earnings</div>
            <div className="text-2xl font-bold text-blue-600">${stats?.gross_earnings.toFixed(2) || '0.00'}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-sm text-gray-600 mb-1">Total Tips</div>
            <div className="text-2xl font-bold text-green-600">${stats?.total_tips.toFixed(2) || '0.00'}</div>
          </CardContent>
        </Card>
      </div>

      {/* Breakdown */}
      <Card>
        <CardHeader>
          <CardTitle>Financial Breakdown</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex justify-between items-center pb-3 border-b">
            <span className="text-gray-700">Base Earnings</span>
            <span className="font-semibold">${stats?.total_earnings.toFixed(2) || '0.00'}</span>
          </div>
          <div className="flex justify-between items-center pb-3 border-b">
            <span className="text-gray-700">Tips</span>
            <span className="font-semibold">${stats?.total_tips.toFixed(2) || '0.00'}</span>
          </div>
          <div className="flex justify-between items-center pb-3 border-b">
            <span className="text-gray-700">Gross Total</span>
            <span className="font-semibold text-lg">${stats?.gross_earnings.toFixed(2) || '0.00'}</span>
          </div>
          <div className="flex justify-between items-center pb-3 border-b">
            <span className="text-orange-600">Commission (20%)</span>
            <span className="font-semibold text-orange-600">-${stats?.total_commission.toFixed(2) || '0.00'}</span>
          </div>
          <div className="flex justify-between items-center pt-2">
            <span className="text-lg font-bold">Net Earnings</span>
            <span className="text-2xl font-bold text-green-600">${stats?.net_earnings.toFixed(2) || '0.00'}</span>
          </div>
        </CardContent>
      </Card>

      {/* Rides Table */}
      <Card>
        <CardHeader className="flex flex-row justify-between items-center">
          <CardTitle>Rides This Week ({rides.length})</CardTitle>
          <Button
            onClick={handleExport}
            disabled={rides.length === 0}
            data-testid="export-csv-button"
          >
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </Button>
        </CardHeader>
        <CardContent>
          {rides.length === 0 ? (
            <p className="text-center py-8 text-gray-500">No rides for this week</p>
          ) : (
            <div className="space-y-3">
              {rides.map((ride) => (
                <div
                  key={ride.id}
                  className="p-4 border rounded-lg hover:bg-gray-50 transition-colors"
                  data-testid={`weekly-ride-${ride.id}`}
                >
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <div className="font-semibold">{ride.client_name}</div>
                      <div className="text-sm text-gray-600">
                        {format(new Date(ride.date), 'EEE, MMM dd, yyyy')}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-bold text-green-600">${ride.net_earnings.toFixed(2)}</div>
                      <div className="text-xs text-gray-500">net</div>
                    </div>
                  </div>
                  <div className="text-sm text-gray-600">
                    {ride.pickup_location} → {ride.dropoff_location}
                  </div>
                  <div className="flex gap-4 mt-2 text-xs text-gray-500">
                    <span>{ride.miles_driven} mi</span>
                    <span>•</span>
                    <span>${ride.total_amount.toFixed(2)} total</span>
                    <span>•</span>
                    <span>${ride.commission_amount.toFixed(2)} commission</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default WeeklyReport;
